// Standings ranked by wallet points. Deliberately a quiet ranked ledger, not a
// neon grid: rows, a best-streak column, and the caller's own row pinned.
import { useMemo } from 'react';

import { Api } from '../lib/api.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { ErrorState, Skeleton, Empty } from '../components/ui.jsx';

function short(addr) {
  return addr ? `${addr.slice(0, 6)}\u2026${addr.slice(-4)}` : '';
}

// Rank glyph: a plain number, with a small ordinal marker for the top three.
// No medals, no confetti; the position is the reward.
function RankCell({ rank }) {
  const tier = rank === 1 ? 'gold' : rank === 2 ? 'silver' : rank === 3 ? 'bronze' : null;
  return (
    <span className={`lb__rank${tier ? ` lb__rank--${tier}` : ''}`}>
      {rank}
    </span>
  );
}

export function Leaderboard() {
  const { sessionReady, address } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.leaderboard(), [sessionReady], { skip: !sessionReady });

  const me = useMemo(() => (address ? address.toLowerCase() : null), [address]);

  if (error) return <ErrorState error={error} onRetry={reload} context="The leaderboard could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={8} />;

  const entries = data.entries ?? [];
  const mine = data.me ?? null;

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Standings</h1>
          <p className="page-subtitle">
            Ranked by points earned on chain. Every number is read live from opBNB, not a cached score.
            {data.count > entries.length ? ` Showing the top ${entries.length} of ${data.count} active wallets.` : ''}
          </p>
        </div>
      </header>

      {mine && mine.rank > 10 && (
        <div className="lb__mecard">
          <span className="lb__mecard-label">Your position</span>
          <span className="lb__mecard-rank">#{mine.rank}</span>
          <span className="lb__mecard-sep" />
          <span className="lb__mecard-stat"><strong>{mine.points}</strong> points</span>
          <span className="lb__mecard-stat">{mine.currentStreak}d streak</span>
          <span className="lb__mecard-stat">{mine.bestStreak ?? 0}d best</span>
        </div>
      )}

      {entries.length === 0 ? (
        <Empty
          title="No standings yet"
          description="Check in from the Home tab to put your wallet on the board. Points are earned on chain."
        />
      ) : (
        <div className="lb">
          <div className="lb__head" role="row">
            <span className="lb__col lb__col--rank">#</span>
            <span className="lb__col lb__col--who">Wallet</span>
            <span className="lb__col lb__col--num">Points</span>
            <span className="lb__col lb__col--num lb__col--opt">Streak</span>
            <span className="lb__col lb__col--num lb__col--opt">Check-ins</span>
            <span className="lb__col lb__col--num">Best</span>
          </div>
          {entries.map((e) => (
            <div
              key={e.address}
              className={`lb__row${e.isMe ? ' lb__row--me' : ''}`}
              role="row"
            >
              <span className="lb__col lb__col--rank"><RankCell rank={e.rank} /></span>
              <span className="lb__col lb__col--who mono">
                {short(e.address)}
                {e.isMe && <span className="lb__youtag">you</span>}
              </span>
              <span className="lb__col lb__col--num lb__points">{e.points}</span>
              <span className="lb__col lb__col--num lb__col--opt">{e.currentStreak}d</span>
              <span className="lb__col lb__col--num lb__col--opt">{e.checkInCount}</span>
              <span className="lb__col lb__col--num">{e.bestStreak ?? 0}d</span>
            </div>
          ))}
        </div>
      )}

      <p className="lb__foot text-muted">
        Check-in awards grow with your streak, so the board rewards consistency, not volume. Points cannot be
        transferred or bought.
      </p>
    </div>
  );
}
